import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api, { fmtIDR } from "../lib/api";
import { fmtUSD, fmtUSDT, usdtAmount, useCurrency, paypalFee } from "../lib/currency";
import { useI18n } from "../lib/i18n";
import { inputCls } from "./admin/ui";

const NEEDS_CHAR = { mail: "checkout.character", trade: "checkout.character", auction: "checkout.character", topup: "checkout.account_id" };

function Row({ label, value, strong, testId }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-[#A8A8AD]">{label}</span>
      <span data-testid={testId} className={strong ? "text-[#D4AF37] font-bold text-lg" : "text-[#F5F3ED] text-right"}>{value}</span>
    </div>
  );
}

function BalanceOption({ balance, checked, onChange }) {
  const { t } = useI18n();
  if (!balance) return null;
  return (
    <label data-testid="checkout-use-balance" className="flex items-center gap-2 border border-[#35353B] rounded-sm p-3 cursor-pointer hover:border-[#D4AF37]/60">
      <input data-testid="checkout-use-balance-checkbox" type="checkbox" className="accent-[#D4AF37]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm">{t("checkout.use_balance")}</span>
      <span className="ml-auto text-xs text-[#A8A8AD]">{t("checkout.balance_available")}: <span className="text-[#D4AF37]">{fmtIDR(balance)}</span></span>
    </label>
  );
}

export default function CheckoutDialog({ open, onOpenChange, product, qty, paymentMethod, onConfirm, submitting }) {
  const { t } = useI18n();
  const { money, fx } = useCurrency();
  const methods = product.delivery_methods || [];
  const [method, setMethod] = useState(methods.length === 1 ? methods[0] : "");
  const [character, setCharacter] = useState("");
  const [note, setNote] = useState("");
  const [balance, setBalance] = useState(0);
  const [useBalance, setUseBalance] = useState(false);
  useEffect(() => { if (open) { api.get("/balance").then((r) => setBalance(r.data.balance_idr)).catch(() => {}); } }, [open]);

  const charKey = NEEDS_CHAR[method];
  const total = product.sell_price_idr * qty;
  const used = useBalance ? Math.min(balance, total) : 0;
  const pct = paymentMethod?.fee_percent || 0;
  const fee = paypalFee(total - used, pct);
  const due = total - used + fee;
  const usd = paymentMethod?.currency === "USD";
  const usdt = paymentMethod?.asset === "USDT";
  const dueLabel = usd ? (fx ? (usdt ? fmtUSDT(usdtAmount(due, fx.rate)) : fmtUSD(due / fx.rate)) : "…") : fmtIDR(due);
  const valid = method && (!charKey || character.trim()) && (!usd || fx || due === 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-dialog" className="bg-[#202127] border-[#35353B] text-[#F5F3ED] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>{t("checkout.title")}</DialogTitle>
          <DialogDescription className="text-[#A8A8AD]">{t("checkout.sub")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t("checkout.method")} *</div>
          {methods.map((m) => (
            <label key={m} data-testid={`delivery-option-${m}`} className={`block border rounded-sm p-3 cursor-pointer transition-colors ${method === m ? "border-[#D4AF37] bg-[#191A1F]" : "border-[#35353B] hover:border-[#D4AF37]/60"}`}>
              <div className="flex items-center gap-2">
                <input type="radio" name="delivery" className="accent-[#D4AF37]" checked={method === m} onChange={() => setMethod(m)} />
                <span className="text-sm font-semibold">{t(`delivery.${m}`)}</span>
              </div>
              <p className="text-xs text-[#A8A8AD] mt-1 ml-5">{t(`delivery.${m}.desc`)}</p>
            </label>
          ))}
        </div>

        {charKey && (
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t(charKey)} *</span>
            <input data-testid="delivery-character-input" maxLength={64} value={character} onChange={(e) => setCharacter(e.target.value)} className={inputCls} />
          </label>
        )}
        {method && (
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{method === "auction" ? t("checkout.auction_notes") : t("checkout.notes")}</span>
            <textarea data-testid="delivery-notes-input" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
          </label>
        )}

        <BalanceOption balance={balance} checked={useBalance} onChange={setUseBalance} />

        <div data-testid="checkout-summary" className="border-t border-[#35353B] pt-3">
          <Row label={t("checkout.product")} value={product.name} />
          <Row label={t("checkout.game_server")} value={`${product.game_name || "-"}${product.server_name ? ` · ${product.server_name}` : ""}`} />
          <Row label={t("checkout.qty")} value={`${qty} × ${product.unit}`} />
          <Row label={t("checkout.unit_price")} value={money(product.sell_price_idr)} />
          <Row label={t("checkout.grand_total")} value={fmtIDR(total)} testId="checkout-grand-total" />
          {used > 0 && <Row label={t("checkout.balance_used")} value={`- ${fmtIDR(used)}`} testId="checkout-balance-used" />}
          {pct > 0 && <Row label={`Biaya ${paymentMethod.name} ${pct}%`} value={`+ ${fmtIDR(fee)}`} testId="checkout-paypal-fee" />}
          {pct > 0 && <Row label="Total akhir (IDR)" value={fmtIDR(due)} testId="checkout-final-idr" />}
          <Row label={t("checkout.payment")} value={`${paymentMethod?.name || "-"} (${usdt ? `USDT${paymentMethod.network ? ` ${paymentMethod.network}` : ""}` : paymentMethod?.currency || "IDR"})`} />
          <Row label={t("checkout.amount_due")} value={dueLabel} strong testId="checkout-amount-due" />
          {usd && fx && due > 0 && <p data-testid="checkout-usd-note" className="text-xs text-[#A8A8AD] mt-1">{usdt ? t("checkout.usdt_note").replace("{rate}", fmtIDR(fx.rate)) : `${t("checkout.usd_note")} $1 = ${fmtIDR(fx.rate)}`}</p>}
          {pct > 0 && <p data-testid="checkout-paypal-note" className="text-xs text-[#A8A8AD] mt-1">{paymentMethod.auto_verify ? "Setelah pesanan dibuat, Anda diarahkan ke PayPal. Pembayaran diverifikasi otomatis." : "Instruksi pembayaran PayPal tampil setelah pesanan dibuat. Pembayaran diverifikasi manual oleh Admin."}</p>}
        </div>

        <button data-testid="checkout-confirm-btn" disabled={!valid || submitting}
          onClick={() => onConfirm({ method, character_name: character.trim(), notes: note.trim() }, used > 0)}
          className="w-full bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-bold rounded-sm py-3">
          {submitting ? t("checkout.processing") : t("checkout.confirm")}
        </button>
      </DialogContent>
    </Dialog>
  );
}
